/**
 * Maps API endpoints to audit entity types and resolves audit events from
 * axios request/response pairs. Pure module — no axios/apiClient import, so
 * it is safe to import in jest without mocking.
 *
 * Consumed by the interceptors in apiClient.js: after a successful mutation
 * (or before a DELETE), resolveAuditEvent() builds the payload for
 * POST /audit and the audit Lambda fetches the authoritative row snapshot
 * itself (see lambda/mysql-audit.mjs ENTITY_MAP).
 */

const MUTATION_METHODS = ["post", "put", "delete"];
const SUMMARY_MAX = 2000;

/**
 * endpoint path -> audit route config.
 * - entityType: key the audit Lambda uses to pick the table to snapshot
 * - idKeys: entity-id field names looked up in request body / query string
 * - responseIdKeys: field names looked up in the response body (creates);
 *   defaults to idKeys + generic "insertId" when omitted
 * - rpc: endpoint also carries RPC-style payloads with no single entity id
 *   (e.g. tag open/close/transfer batches) — audit those as action "rpc"
 * - skip: predicate on the request body; true = don't audit this call
 *   (e.g. POST /inquiry with checkJobName is a live search, not a save)
 */
export const AUDIT_ROUTES = {
  "/bol": { entityType: "bol", idKeys: ["BOLID"] },
  "/inquiry": {
    entityType: "inquiry",
    idKeys: ["JobTicketID"],
    // insertId omitted on purpose: POST /inquiry also creates equipment rows
    // whose insertId is NOT a JobTicketID — those degrade to a tombstone.
    responseIdKeys: ["JobTicketID"],
    skip: (body) => Boolean(body && body.checkJobName),
  },
  "/customer": { entityType: "customer", idKeys: ["CustomerID"] },
  "/contact": { entityType: "contact", idKeys: ["ContactID"] },
  "/location": { entityType: "location", idKeys: ["LocationID"] },
  "/inventory": {
    entityType: "inventory-item",
    idKeys: ["InventoryID", "TagID"],
    rpc: true,
  },
  "/inventory/item": {
    entityType: "inventory-item",
    idKeys: ["InventoryID", "TagID"],
    rpc: true,
  },
  "/time-entry": { entityType: "time-entry", idKeys: ["TimeEntryID"] },
};

/**
 * Reduce an axios config.url (relative "/inventory?x=1" or absolute
 * "https://...execute-api.../dev/inventory") to the bare endpoint path.
 */
export function normalizePath(url) {
  if (!url) return "";
  let path = String(url);
  const apiBase = process.env.REACT_APP_API || "";
  if (apiBase && path.startsWith(apiBase)) {
    path = path.slice(apiBase.length);
  } else if (/^https?:\/\//i.test(path)) {
    try {
      path = new URL(path).pathname;
    } catch {
      return "";
    }
  }
  const queryIndex = path.indexOf("?");
  if (queryIndex !== -1) path = path.slice(0, queryIndex);
  if (!path.startsWith("/")) path = `/${path}`;
  return path;
}

function idFromObject(obj, keys) {
  if (!obj || typeof obj !== "object") return null;
  for (const key of keys) {
    const value = obj[key];
    if (value !== undefined && value !== null && value !== "") return value;
  }
  return null;
}

function idFromQueryString(url, keys) {
  if (!url) return null;
  const queryIndex = url.indexOf("?");
  if (queryIndex === -1) return null;
  const params = new URLSearchParams(url.slice(queryIndex + 1));
  for (const key of keys) {
    const value = params.get(key);
    if (value) return value;
  }
  return null;
}

function parseBody(data) {
  if (data == null) return null;
  if (typeof data === "string") {
    try {
      return JSON.parse(data);
    } catch {
      return null;
    }
  }
  return typeof data === "object" ? data : null;
}

/** Keep the client-side payload copy small; the Lambda caps it again. */
function summarize(body) {
  if (!body || typeof body !== "object") return null;
  const str = JSON.stringify(body);
  if (str.length <= SUMMARY_MAX) return body;
  return { _truncated: true, _keys: Object.keys(body) };
}

/**
 * Build the POST /audit payload for a mutation, or null when the call is
 * not auditable (unmapped endpoint, non-mutation method, skipped search
 * calls, audit's own traffic).
 *
 * @param {object} config axios request config ({ method, url, data })
 * @param {object|null} responseData response body (null for pre-delete)
 */
export function resolveAuditEvent(config, responseData) {
  if (!config || config.__skipAudit) return null;

  const method = (config.method || "").toLowerCase();
  if (!MUTATION_METHODS.includes(method)) return null;

  const endpoint = normalizePath(config.url);
  if (!endpoint || endpoint.startsWith("/audit")) return null;

  const route = AUDIT_ROUTES[endpoint];
  if (!route) return null;

  const body = parseBody(config.data);
  if (route.skip && route.skip(body)) return null;

  const responseIdKeys = route.responseIdKeys || [...route.idKeys, "insertId"];
  const entityId =
    idFromObject(body, route.idKeys) ??
    idFromQueryString(config.url, route.idKeys) ??
    idFromObject(parseBody(responseData), responseIdKeys);

  let action;
  if (method === "delete") action = "delete";
  else if (entityId == null && route.rpc) action = "rpc";
  else action = method === "post" ? "create" : "update";

  return {
    entityType: route.entityType,
    entityId: entityId != null ? String(entityId) : null,
    action,
    endpoint,
    method: method.toUpperCase(),
    requestSummary: summarize(body),
  };
}
